/**
 * Calendario propio del panel.
 *
 * Sustituye a `<input type="date">`, `<input type="datetime-local">` y
 * `<input type="month">`, cuyo calendario lo dibuja el sistema operativo (en la
 * captura salía un calendario gris, con la tipografía del navegador y sin
 * relación con el diseño del panel).
 *
 * El valor que maneja es el mismo del nativo:
 * - `date` → `AAAA-MM-DD`
 * - `datetime-local` → `AAAA-MM-DDTHH:mm`
 * - `month` → `AAAA-MM`
 *
 * Se usa desde `Input` (los tres tipos se interceptan allí) y directamente como
 * `CampoFecha` cuando hace falta dentro de otro contenedor.
 */

import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';
import { posicionarFlotante } from '@/lib/flotante';

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const MESES_CORTOS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const DIAS = ['LU', 'MA', 'MI', 'JU', 'VI', 'SA', 'DO'];

export type TipoFecha = 'date' | 'datetime-local' | 'month' | 'time';

/** `AAAA-MM-DD` del día indicado, con la fecha local (no `toISOString`). */
function iso(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Parte el valor del campo en sus trozos (`AAAA-MM-DD`, `HH:mm`). */
function partirValor(valor: string, tipo: TipoFecha) {
  if (tipo === 'time') {
    const [hora, minuto] = valor.split(':');

    return {
      anio: null,
      mes: null,
      dia: null,
      hora: hora?.slice(0, 2) || null,
      minuto: minuto?.slice(0, 2) || null,
    };
  }

  if (tipo === 'month') {
    const [anio, mes] = valor.split('-');

    return { anio: Number(anio) || null, mes: Number(mes) || null, dia: null, hora: null, minuto: null };
  }

  const [fecha, hora] = valor.split('T');
  const [anio, mes, dia] = (fecha ?? '').split('-');

  return {
    anio: Number(anio) || null,
    mes: Number(mes) || null,
    dia: Number(dia) || null,
    hora: hora?.slice(0, 2) ?? null,
    minuto: hora?.slice(3, 5) ?? null,
  };
}

/** Texto que se muestra en el disparador. */
function textoDeValor(valor: string, tipo: TipoFecha): string {
  const { anio, mes, dia, hora, minuto } = partirValor(valor, tipo);

  if (tipo === 'time') {
    return hora && minuto ? `${hora.padStart(2, '0')}:${minuto.padStart(2, '0')}` : '';
  }

  if (!anio || !mes) return '';

  if (tipo === 'month') return `${MESES[mes - 1]} ${anio}`;

  const fecha = `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${anio}`;

  if (tipo === 'datetime-local') {
    return `${fecha} ${hora ?? '00'}:${minuto ?? '00'}`;
  }

  return fecha;
}

/** Rejilla del mes: 6 semanas empezando en lunes. */
function diasDeLaCuadricula(anio: number, mes: number): { fecha: string; dia: number; delMes: boolean }[] {
  const primero = new Date(anio, mes - 1, 1);
  // `getDay()` devuelve 0 para domingo; la rejilla empieza en lunes.
  const desplazamiento = (primero.getDay() + 6) % 7;
  const inicio = new Date(anio, mes - 1, 1 - desplazamiento);

  return Array.from({ length: 42 }, (_, indice) => {
    const fecha = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + indice);

    return {
      fecha: iso(fecha),
      dia: fecha.getDate(),
      delMes: fecha.getMonth() === mes - 1,
    };
  });
}

export interface CampoFechaProps {
  id?: string;
  name?: string;
  tipo: TipoFecha;
  value: string;
  onChange?: (evento: { target: { value: string } }) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Fecha mínima y máxima admitidas (`AAAA-MM-DD`), como en el nativo. */
  min?: string;
  max?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
}

/**
 * Campo de fecha con calendario propio.
 *
 * Mantiene la forma del `<input>` nativo: `value` y `onChange` con
 * `target.value` en el formato del tipo elegido.
 */
export function CampoFecha({
  id,
  name,
  tipo,
  value,
  onChange,
  disabled,
  placeholder,
  className,
  min,
  max,
  ...aria
}: CampoFechaProps) {
  const hoy = new Date();
  const partes = partirValor(value, tipo);

  const [abierto, setAbierto] = useState(false);
  const [posicion, setPosicion] = useState<{ top: number; left: number } | null>(null);
  const [mesVisible, setMesVisible] = useState(() => ({
    anio: partes.anio ?? hoy.getFullYear(),
    mes: partes.mes ?? hoy.getMonth() + 1,
  }));
  const [anioVisible, setAnioVisible] = useState(partes.anio ?? hoy.getFullYear());
  const [hora, setHora] = useState(partes.hora ?? '00');
  const [minuto, setMinuto] = useState(partes.minuto ?? '00');
  const disparador = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const emitir = (nuevoValor: string) => onChange?.({ target: { value: nuevoValor } });

  // Al abrir se sitúa el calendario en el mes del valor (o en el actual).
  useEffect(() => {
    if (!abierto) return;

    const referencia = partirValor(value, tipo);

    setMesVisible({
      anio: referencia.anio ?? hoy.getFullYear(),
      mes: referencia.mes ?? hoy.getMonth() + 1,
    });
    setAnioVisible(referencia.anio ?? hoy.getFullYear());
    setHora(referencia.hora ?? '00');
    setMinuto(referencia.minuto ?? '00');
    // `hoy` cambia en cada render; basta con el valor y el tipo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, value, tipo]);

  useEffect(() => {
    if (!abierto) return undefined;

    const alClicFuera = (evento: MouseEvent) => {
      if (disparador.current?.contains(evento.target as Node)) return;
      if (panel.current?.contains(evento.target as Node)) return;

      setAbierto(false);
    };

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        setAbierto(false);
        disparador.current?.focus();
      }
    };

    const alDesplazar = (evento: Event) => {
      const destino = evento.target as Node | null;

      if (destino && panel.current?.contains(destino)) return;

      setAbierto(false);
    };

    document.addEventListener('mousedown', alClicFuera);
    document.addEventListener('keydown', alTeclear);
    window.addEventListener('scroll', alDesplazar, true);

    return () => {
      document.removeEventListener('mousedown', alClicFuera);
      document.removeEventListener('keydown', alTeclear);
      window.removeEventListener('scroll', alDesplazar, true);
    };
  }, [abierto, id, tipo]);

  const abrir = () => {
    const caja = disparador.current?.getBoundingClientRect();

    if (!caja) return;

    const ancho = tipo === 'month' ? 260 : tipo === 'time' ? 220 : 288;
    // Alto aproximado: cabecera, rejilla de 6 semanas y pie (más la fila de hora
    // cuando es fecha y hora).
    const alto = tipo === 'datetime-local' ? 380 : tipo === 'time' ? 160 : 340;
    const { top, left } = posicionarFlotante(caja, alto, ancho);

    setPosicion({ top, left });
    setAbierto(true);
  };

  const texto = textoDeValor(value, tipo);

  /** Elige un día (o un mes) y cierra; con hora, se emite al confirmar. */
  const elegirDia = (fecha: string) => {
    if (tipo === 'datetime-local') {
      emitir(`${fecha}T${hora}:${minuto}`);

      return;
    }

    emitir(fecha);
    setAbierto(false);
    disparador.current?.focus();
  };

  const moverMes = (salto: number) => {
    setMesVisible((actual) => {
      const referencia = new Date(actual.anio, actual.mes - 1 + salto, 1);

      return { anio: referencia.getFullYear(), mes: referencia.getMonth() + 1 };
    });
  };

  const irAHoy = () => {
    if (tipo === 'month') {
      emitir(iso(hoy).slice(0, 7));

      return;
    }

    elegirDia(iso(hoy));
  };

  const dias = tipo === 'month' || tipo === 'time' ? [] : diasDeLaCuadricula(mesVisible.anio, mesVisible.mes);

  return (
    <>
      <button
        {...aria}
        ref={disparador}
        id={id}
        name={name}
        type="button"
        disabled={disabled ?? false}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        className={cn(
          'flex items-center gap-2 text-left',
          !texto && 'text-ink-400',
          disabled && 'cursor-not-allowed',
          className,
        )}
        onClick={() => (abierto ? setAbierto(false) : abrir())}
      >
        <span className="truncate">
          {texto ||
            placeholder ||
            (tipo === 'month' ? 'Mes' : tipo === 'time' ? '--:--' : 'dd/mm/aaaa')}
        </span>
        {tipo === 'datetime-local' ? (
          <Clock className="size-4 shrink-0 text-ink-400" aria-hidden="true" />
        ) : (
          <CalendarDays className="size-4 shrink-0 text-ink-400" aria-hidden="true" />
        )}
      </button>

      {abierto && posicion
        ? createPortal(
            <div
              id={`${id ?? tipo}-calendario`}
              ref={panel}
              role="dialog"
              aria-label={tipo === 'month' ? 'Elegir mes' : 'Elegir fecha'}
              style={{ top: posicion.top, left: posicion.left }}
              className={cn(
                'fixed z-50 rounded-card border border-ink-200 bg-white p-3 shadow-xl',
                tipo === 'month' ? 'w-[260px]' : 'w-72',
              )}
            >
              <div className={cn('mb-2 flex items-center justify-between gap-1', tipo === 'time' && 'hidden')}>
                <button
                  type="button"
                  className="rounded-control p-1 text-ink-500 hover:bg-ink-50 hover:text-ink-800"
                  aria-label={tipo === 'month' ? 'Año anterior' : 'Mes anterior'}
                  onClick={() => (tipo === 'month' ? setAnioVisible((anio) => anio - 1) : moverMes(-1))}
                >
                  <ChevronLeft className="size-4" aria-hidden="true" />
                </button>

                <p className="text-sm font-semibold text-ink-800">
                  {tipo === 'month'
                    ? anioVisible
                    : `${MESES[mesVisible.mes - 1]} ${mesVisible.anio}`}
                </p>

                <button
                  type="button"
                  className="rounded-control p-1 text-ink-500 hover:bg-ink-50 hover:text-ink-800"
                  aria-label={tipo === 'month' ? 'Año siguiente' : 'Mes siguiente'}
                  onClick={() => (tipo === 'month' ? setAnioVisible((anio) => anio + 1) : moverMes(1))}
                >
                  <ChevronRight className="size-4" aria-hidden="true" />
                </button>
              </div>

              {tipo === 'time' ? (
                    <div className="flex items-end gap-2">
                      <label className="flex flex-col gap-1 text-[11px] text-ink-500">
                        Hora
                        <input
                          className="h-9 w-16 rounded-control border border-ink-200 px-2 text-center text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                          inputMode="numeric"
                          maxLength={2}
                          value={hora}
                          onChange={(evento) =>
                            setHora(evento.target.value.replace(/\D/g, '').slice(0, 2))
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-[11px] text-ink-500">
                        Minuto
                        <input
                          className="h-9 w-16 rounded-control border border-ink-200 px-2 text-center text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                          inputMode="numeric"
                          maxLength={2}
                          value={minuto}
                          onChange={(evento) =>
                            setMinuto(evento.target.value.replace(/\D/g, '').slice(0, 2))
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className="ml-auto rounded-control bg-brand-500 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
                        onClick={() => emitir(`${hora.padStart(2, '0')}:${minuto.padStart(2, '0')}`)}
                      >
                        Aplicar
                      </button>
                    </div>
                  ) : tipo === 'month' ? (
                <div className="grid grid-cols-3 gap-1">
                  {MESES_CORTOS.map((nombre, indice) => {
                    const elegido = partes.anio === anioVisible && partes.mes === indice + 1;

                    return (
                      <button
                        key={nombre}
                        type="button"
                        className={cn(
                          'rounded-control py-2 text-xs font-semibold transition-colors',
                          elegido
                            ? 'bg-brand-500 text-white'
                            : 'text-ink-700 hover:bg-brand-50 hover:text-brand-700',
                        )}
                        onClick={() => {
                          emitir(`${anioVisible}-${String(indice + 1).padStart(2, '0')}`);
                          setAbierto(false);
                        }}
                      >
                        {nombre}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-0.5 text-center">
                    {DIAS.map((dia) => (
                      <span key={dia} className="pb-1 text-[10px] font-semibold tracking-wide text-ink-400">
                        {dia}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-0.5">
                    {dias.map((dia) => {
                      const elegido = value.slice(0, 10) === dia.fecha;
                      const esHoy = dia.fecha === iso(hoy);
                      // Fuera del rango admitido: como el calendario nativo,
                      // el día se ve pero no se puede elegir.
                      const fuera = Boolean((min && dia.fecha < min) || (max && dia.fecha > max));

                      return (
                        <button
                          key={dia.fecha}
                          type="button"
                          disabled={fuera}
                          aria-current={esHoy ? 'date' : undefined}
                          className={cn(
                            'rounded-control py-1.5 text-center text-xs font-medium transition-colors',
                            fuera
                              ? 'cursor-not-allowed text-ink-200'
                              : elegido
                                ? 'bg-brand-500 text-white'
                                : dia.delMes
                                  ? 'text-ink-800 hover:bg-brand-50 hover:text-brand-700'
                                  : 'text-ink-300 hover:bg-ink-50',
                            !fuera && !elegido && esHoy && 'ring-1 ring-brand-300',
                          )}
                          onClick={() => elegirDia(dia.fecha)}
                        >
                          {dia.dia}
                        </button>
                      );
                    })}
                  </div>

                  {tipo === 'datetime-local' ? (
                    <div className="mt-2 flex items-end gap-2 border-t border-ink-100 pt-2">
                      <label className="flex flex-col gap-1 text-[11px] text-ink-500">
                        Hora
                        <input
                          className="h-9 w-16 rounded-control border border-ink-200 px-2 text-center text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                          inputMode="numeric"
                          maxLength={2}
                          value={hora}
                          onChange={(evento) =>
                            setHora(evento.target.value.replace(/\D/g, '').slice(0, 2))
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-[11px] text-ink-500">
                        Minuto
                        <input
                          className="h-9 w-16 rounded-control border border-ink-200 px-2 text-center text-sm text-ink-900 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                          inputMode="numeric"
                          maxLength={2}
                          value={minuto}
                          onChange={(evento) =>
                            setMinuto(evento.target.value.replace(/\D/g, '').slice(0, 2))
                          }
                        />
                      </label>

                      <button
                        type="button"
                        className="ml-auto rounded-control bg-brand-500 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600"
                        onClick={() => {
                          const fecha = value.slice(0, 10) || iso(hoy);

                          emitir(
                            `${fecha}T${hora.padStart(2, '0')}:${minuto.padStart(2, '0')}`,
                          );
                          setAbierto(false);
                        }}
                      >
                        Aplicar
                      </button>
                    </div>
                  ) : null}
                </>
              )}

              <div className="mt-2 flex items-center justify-between gap-2 border-t border-ink-100 pt-2">
                <button
                  type="button"
                  className="rounded-control px-2 py-1 text-xs font-medium text-ink-500 hover:bg-ink-50 hover:text-ink-700"
                  onClick={() => {
                    emitir('');
                    setAbierto(false);
                  }}
                >
                  Borrar
                </button>
                {tipo === 'time' ? (
                  <button
                    type="button"
                    className="rounded-control px-2 py-1 text-xs font-semibold text-brand-600 hover:bg-brand-50"
                    onClick={() => {
                      emitir(`${String(hoy.getHours()).padStart(2, '0')}:${String(hoy.getMinutes()).padStart(2, '0')}`);
                      setAbierto(false);
                    }}
                  >
                    Ahora
                  </button>
                ) : (
                  <button
                    type="button"
                    className="rounded-control px-2 py-1 text-xs font-semibold text-brand-600 hover:bg-brand-50"
                    onClick={irAHoy}
                  >
                    {tipo === 'month' ? 'Este mes' : 'Hoy'}
                  </button>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Años del selector rápido (por si otra pantalla lo necesita). */
export function rangoDeAnios(centro: number, radio = 6): number[] {
  return Array.from({ length: radio * 2 + 1 }, (_, indice) => centro - radio + indice);
}
